
import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations } from '../i18n/translations';
import { TranslationsObject } from '../i18n/types';

export type Language = 'ka' | 'en' | 'ru' | 'tr';

export interface LanguageContextType {
  language: Language;
  currentLanguage: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  console.log("LanguageProvider rendering...");
  
  const getInitialLanguage = (): Language => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem('language') as Language;
        const validLanguages: Language[] = ['ka', 'en', 'ru', 'tr'];
        if (saved && validLanguages.includes(saved)) {
          console.log("LanguageProvider: Loaded saved language:", saved);
          return saved;
        }
      }
    } catch (error) {
      console.warn('LanguageProvider: Error loading language from localStorage', error);
    }
    console.log("LanguageProvider: Using default language: ka");
    return 'ka';
  };

  const [language, setLanguage] = useState<Language>(() => getInitialLanguage());

  useEffect(() => {
    console.log("LanguageProvider: Applying language effect for:", language);
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('language', language);
      }
      document.documentElement.lang = language;
      console.log("Language applied successfully:", language);
    } catch (error) {
      console.error("Error applying language:", error);
    }
  }, [language]);

  const t = (key: string): string => {
    try {
      const translationObj = translations[language] as TranslationsObject;
      return translationObj[key] || key;
    } catch (error) {
      console.error("Translation error for key:", key, error);
      return key;
    }
  };

  const handleSetLanguage = (lang: Language) => {
    console.log("LanguageProvider: Setting language to:", lang);
    setLanguage(lang);
  };

  const value = {
    language,
    currentLanguage: language,
    setLanguage: handleSetLanguage,
    t
  };

  console.log("LanguageProvider: Providing value:", value);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

